/**
 * Shared React Query surface for the pronunciation-dictionary library
 * (SA-19). Every mutation invalidates the list key so all consumers —
 * the admin library page and the persona-editor picker — refetch instead
 * of serving a stale cached snapshot across route/modal transitions.
 * Pattern: hooks/useAdapters.ts.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  archivePronunciationDictionary,
  listPronunciationDictionaries,
  updatePronunciationDictionary,
  uploadPronunciationDictionary,
} from '@/services/pronunciationLibrary'

export const PRONUNCIATION_DICTIONARIES_KEY = ['pronunciation-dictionaries'] as const

export function usePronunciationDictionaries() {
  return useQuery({
    queryKey: PRONUNCIATION_DICTIONARIES_KEY,
    queryFn: listPronunciationDictionaries,
  })
}

export function useUploadPronunciationDictionary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; description?: string; file: File }) =>
      uploadPronunciationDictionary(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PRONUNCIATION_DICTIONARIES_KEY })
    },
  })
}

export function useUpdatePronunciationDictionary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string
      name?: string
      description?: string
      file?: File
    }) => updatePronunciationDictionary(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PRONUNCIATION_DICTIONARIES_KEY })
    },
  })
}

export function useArchivePronunciationDictionary() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => archivePronunciationDictionary(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PRONUNCIATION_DICTIONARIES_KEY })
    },
  })
}
